import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { clearAuthCache } from '../layouts/AuthGate';

const titleMap: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/projects': 'Projects',
  '/map': 'Builder',
  '/builder': 'Builder',
  '/built': 'Built network',
  '/work-orders': 'Work Orders',
  '/crews': 'Crews',
  '/reports': 'Reports',
  '/bead-report': 'BEAD Territory Validation',
  '/settings': 'Plant Settings',
  // Grantor screens. Falling through to the product name told a reviewer nothing
  // about where they were.
  '/grantor': 'Oversight',
  '/grantor/builder': 'De-Scope Builder',
  '/grantor/intake': 'Request Intake',
  '/grantor/ledger': 'Decision Ledger',
  '/grantor/reassignment': 'Reassignment',
  '/descope': 'De-Scope Review',
};

type Me = { id: string; email: string; role: string };

export default function Topbar() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  // 'FiberForge' is the internal name of the engine, not the product a customer
  // bought. An unmapped route should never be where they learn it.
  const title = titleMap[pathname] ?? 'Milestone';

  const { data: me } = useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
    retry: false,
  });

  // Sample-data badge — appears when any sample fixtures exist in DB.
  // Polls every 30s so wipes update the indicator within a window.
  const { data: sampleSummary } = useQuery<{ total_sample_rows: number }>({
    queryKey: ['topbar-sample-summary'],
    queryFn: () => api.get('/admin/sample-data/summary'),
    refetchInterval: 30_000,
    retry: false,
  });
  const sampleRows = sampleSummary?.total_sample_rows ?? 0;

  async function logout() {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearAuthCache();
    nav('/login', { replace: true });
  }

  const initials = me?.email
    ? me.email.split('@')[0].split(/[._-]/).map((s) => s[0]?.toUpperCase() ?? '').join('').slice(0, 2)
    : 'FF';

  return (
    <header className="topbar">
      <div className="topbar__title">{title}</div>
      <div className="topbar__right">
        {sampleRows > 0 && (
          <Link
            to="/settings/sample-data"
            title={`${sampleRows.toLocaleString()} sample fixture rows in DB. Click to manage.`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#fef3c7', color: '#92400e',
              padding: '3px 10px', borderRadius: 4,
              fontSize: 11, fontWeight: 700, letterSpacing: '.04em',
              textDecoration: 'none', border: '1px solid #fde68a',
            }}
          >
            <span style={{ fontSize: 8 }}>●</span>
            SAMPLE DATA
            <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}>{sampleRows.toLocaleString()}</span>
          </Link>
        )}
        <span>Today · {new Date().toLocaleDateString()}</span>
        {me && (
          <div className="topbar__user" title={me.email}>
            <div className="topbar__avatar">{initials}</div>
            <span style={{ fontSize: 13 }}>{me.email}</span>
            <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={logout}>
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
